import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { LeadFormBody } from "@/components/lead-form/LeadFormBody";
import { Step1Testimonial } from "@/components/lead-form/Step1Testimonial";
import { Step2Testimonial } from "@/components/lead-form/Step2Testimonial";
import { Step3Testimonial } from "@/components/lead-form/Step3Testimonial";
import { Step4Testimonial } from "@/components/lead-form/Step4Testimonial";
import { Step5Testimonial } from "@/components/lead-form/Step5Testimonial";
import { Step6Testimonial } from "@/components/lead-form/Step6Testimonial";
import { Step7Testimonial } from "@/components/lead-form/Step7Testimonial";
import mindLogo from "@/assets/mind-media-logo.svg";

const searchSchema = z.object({
  plan: fallback(z.enum(["launch", "growth", "custom"]).optional(), undefined),
  utm_source: fallback(z.string().optional(), undefined),
  utm_medium: fallback(z.string().optional(), undefined),
  utm_campaign: fallback(z.string().optional(), undefined),
  utm_term: fallback(z.string().optional(), undefined),
  utm_content: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/get-mockup")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Get your free website mockup — Mind Media" },
      {
        name: "description",
        content:
          "Tell us about your business and we'll send a free custom website mockup in under 12 hours. No commitment, no payment needed.",
      },
      { property: "og:title", content: "Get your free website mockup — Mind Media" },
      {
        property: "og:description",
        content:
          "Free custom mockup in under 12 hours. No commitment, no payment needed.",
      },
    ],
  }),
  component: GetMockupPage,
});

function GetMockupPage() {
  const { plan } = Route.useSearch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      <h1 className="sr-only">Get your free website mockup</h1>
      <div className="pointer-events-none absolute inset-0 -z-10 hidden lg:block">
        <div className="absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-hero-glow-cool opacity-30 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-hero-glow-warm opacity-25 blur-3xl" />
      </div>

      <div className="mx-auto flex w-full max-w-[1500px] shrink-0 items-center justify-between px-4 py-6 sm:px-6 lg:px-10">
        <Link to="/" className="inline-flex items-center">
          <img src={mindLogo} alt="Mind Media" className="h-7 w-auto" />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      <div className="mx-auto flex w-full max-w-[1500px] flex-1 items-center px-4 pb-16 sm:px-6 lg:px-10">

        <div
          className={
            currentStep >= 1 && currentStep <= 7
              ? currentStep >= 6
                ? "grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-5 lg:gap-8"
                : "grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
              : "flex w-full justify-center"
          }
        >
          <div className={currentStep >= 1 && currentStep <= 7 ? `flex justify-center lg:justify-start${currentStep >= 6 ? " lg:col-span-3" : ""}` : "contents"}>
            <LeadFormBody
              initialPlan={plan}
              onClose={() => navigate({ to: "/" })}
              onStepChange={setCurrentStep}
            />
          </div>
          {currentStep === 1 && (
            <div className="hidden lg:block">
              <Step1Testimonial />
            </div>
          )}
          {currentStep === 2 && (
            <div className="hidden lg:block">
              <Step2Testimonial />
            </div>
          )}
          {currentStep === 3 && (
            <div className="hidden lg:block">
              <Step3Testimonial />
            </div>
          )}
          {currentStep === 4 && (
            <div className="hidden lg:block">
              <Step4Testimonial />
            </div>
          )}
          {currentStep === 5 && (
            <div className="hidden lg:block">
              <Step5Testimonial />
            </div>
          )}
          {currentStep === 6 && (
            <div className="hidden lg:col-span-2 lg:block">
              <Step6Testimonial />
            </div>
          )}
          {currentStep === 7 && (
            <div className="hidden lg:col-span-2 lg:block">
              <Step7Testimonial />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
